import React, { Component } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import DashboardSummaryCards from './DashboardSummaryCards';
import ServiceListTable from './ServiceListTable';
import ServiceDetailModal from './ServiceDetailModal';
import AlertBanner from './AlertBanner';
import AlertActivityPanel from './AlertActivityPanel';
import { getAllServices, markAlertAsViewed, checkMonitoringHealth } from '../../../services/monitoring.service';
import AuthService from '../../Auth/AuthService';
import PermissionFeature from '../../Permissions/PermissionFeature';
import './ServiceMonitoring.css';

/**
 * Service Monitoring Dashboard - Main Container Component
 * Route: /admin/service-monitoring
 *
 * Features:
 * - Checks monitoring health before starting polling
 * - Displays summary cards (total, running, stopped, errors)
 * - Shows service list table with all details
 * - Opens detail modal on service click
 * - Auto-refreshes every 10 seconds (only if monitoring is enabled)
 */
class ServiceMonitoringDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      services: [],
      loading: true,
      error: null,
      selectedService: null,
      showModal: false,
      lastUpdated: null,
      // Alert banner state (from email redirect)
      showAlertBanner: false,
      alertServiceName: null,
      alertId: null,
      // Monitoring health state
      monitoringDisabled: false,
      monitoringMessage: null,
      monitoringRecommendation: null,
    };

    this.refreshInterval = null;
  }

  componentDidMount() {
    //console.log('[ServiceMonitoringDashboard] Component mounting');
    // Check for URL parameters (email deep link)
    this.checkForAlertParameters();

    // Check monitoring health before starting polling
    this.checkMonitoringHealthStatus();
  }

  /**
   * Check if monitoring module is enabled and configured
   * If disabled, show message and don't start polling
   */
  checkMonitoringHealthStatus = async () => {
    try {
      const healthResponse = await checkMonitoringHealth();

      // Check both PascalCase and camelCase for compatibility
      const monitoringEnabled = ('monitoringEnabled' in healthResponse)
        ? healthResponse.monitoringEnabled
        : healthResponse.MonitoringEnabled;

      if (!monitoringEnabled) {
        // Monitoring is disabled - show message and don't poll
        this.setState({
          monitoringDisabled: true,
          monitoringMessage: healthResponse.message || healthResponse.Message,
          monitoringRecommendation: healthResponse.recommendation || healthResponse.Recommendation,
          loading: false,
        });
        console.warn('Service monitoring is disabled:', healthResponse.message || healthResponse.Message);
        return;
      }

      // Monitoring is enabled - proceed with normal operations
      this.fetchServices();

      // Set up auto-refresh every 10 seconds
      this.refreshInterval = setInterval(() => {
        this.fetchServices(true); // silent refresh (no loading spinner)
      }, 10000);

    } catch (error) {
      console.error('Failed to check monitoring health:', error);
      // If health check fails, proceed anyway for backward compatibility
      this.fetchServices();
      this.refreshInterval = setInterval(() => {
        this.fetchServices(true);
      }, 10000);
    }
  };

  /**
   * Check URL parameters for email alert redirect
   * URL format: /admin/service-monitoring?service=ServiceName&alert=true&alertId=123
   */
  checkForAlertParameters = () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const serviceParam = urlParams.get('service');
      const isFromAlert = urlParams.get('alert') === 'true';
      const alertIdParam = urlParams.get('alertId');

      if (isFromAlert && serviceParam && alertIdParam) {
        const alertId = parseInt(alertIdParam, 10);

        if (!isNaN(alertId) && alertId > 0) {
          this.setState({
            showAlertBanner: true,
            alertServiceName: decodeURIComponent(serviceParam),
            alertId: alertId,
          });

          // Mark alert as viewed in database
          this.handleMarkAlertAsViewed(alertId);

          // Auto-open service detail modal for the alerted service
          setTimeout(() => {
            this.handleServiceClick(decodeURIComponent(serviceParam));
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error parsing URL parameters:', error);
    }
  };

  /**
   * Mark an alert as viewed in the database
   * @param {number} alertId - Alert ID to mark as viewed
   */
  handleMarkAlertAsViewed = async (alertId) => {
    try {
      await markAlertAsViewed(alertId);
      //console.log(`Alert ${alertId} marked as viewed`);
    } catch (error) {
      console.error('Failed to mark alert as viewed:', error);
      // Don't show error to user - this is a background operation
    }
  };

  /**
   * Dismiss the alert banner
   */
  handleDismissAlertBanner = () => {
    this.setState({
      showAlertBanner: false,
    });
  };

  componentWillUnmount() {
    // Clean up interval on unmount
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  /**
   * Fetch all services from API
   * @param {boolean} silent - If true, don't show loading spinner
   */
  fetchServices = async (silent = false) => {
    try {
      //console.log('[ServiceMonitoringDashboard] Fetching services... (silent:', silent, ')');
      if (!silent) {
        this.setState({ loading: true });
      }

      const response = await getAllServices();
      const services = response.Data || response.data || response;

      //console.log('[ServiceMonitoringDashboard] Fetched', services.length, 'services');

      this.setState({
        services,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (error) {
      console.error('[ServiceMonitoringDashboard] Error fetching services:', error);

      this.setState({
        loading: false,
        error: error.message || 'Failed to fetch services',
      });

      // Show error toast
      toast.error('Failed to fetch service data. Please check if the API is running.');
    }
  };

  /**
   * Handle service click - open detail modal
   * @param {string} serviceName - Name of service to show details for
   */
  handleServiceClick = (serviceName) => {
    this.setState({
      selectedService: serviceName,
      showModal: true,
    });
  };

  /**
   * Close detail modal
   */
  handleCloseModal = () => {
    this.setState({
      showModal: false,
      selectedService: null,
    });
  };

  /**
   * Manual refresh button handler
   */
  handleRefresh = () => {
    this.fetchServices();
  };

  /**
   * Retry monitoring health check (after configuration fix)
   */
  handleRetryMonitoring = () => {
    this.setState({
      loading: true,
      monitoringDisabled: false,
      monitoringMessage: null,
      monitoringRecommendation: null,
    });
    this.checkMonitoringHealthStatus();
  };

  render() {
    const {
      services,
      loading,
      error,
      showModal,
      selectedService,
      lastUpdated,
      showAlertBanner,
      alertServiceName,
      alertId,
      monitoringDisabled,
      monitoringMessage,
      monitoringRecommendation,
    } = this.state;

    // Show monitoring disabled message
    if (monitoringDisabled) {
      return (
        <div className="service-monitoring-dashboard">
          <Container fluid>
            <Row className="mb-4">
              <Col>
                <h2>Service Monitoring Dashboard</h2>
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="alert alert-warning border-warning" style={{ borderLeft: '4px solid' }}>
                  <div className="d-flex align-items-start">
                    <div className="mr-3" style={{ fontSize: '2.5rem' }}>
                      <i className="fa fa-exclamation-triangle text-warning"></i>
                    </div>
                    <div className="flex-grow-1">
                      <h4 className="alert-heading">
                        <i className="fa fa-power-off mr-2"></i>
                        Service Monitoring Module Disabled
                      </h4>
                      <p className="mb-2">{monitoringMessage}</p>
                      <hr />
                      <p className="mb-3">
                        <strong>
                          <i className="fa fa-lightbulb-o mr-2"></i>
                          Recommendation:
                        </strong>
                        <br />
                        {monitoringRecommendation}
                      </p>
                      <div className="alert alert-info mb-3">
                        <strong>Quick Fix:</strong> Add the following to your <code>appsettings.json</code>:
                        <pre className="bg-dark text-light p-3 mt-2 mb-0" style={{ borderRadius: '4px' }}>
{`"ServiceMonitoring": {
  "MonitoringDatabase": "Data Source=c:\\\\path\\\\to\\\\monitoring-DEV.db;Version=3;"
}`}
                        </pre>
                      </div>
                      <button
                        className="btn btn-primary"
                        onClick={this.handleRetryMonitoring}
                      >
                        <i className="fa fa-refresh mr-2"></i>
                        Retry Connection
                      </button>
                      <button
                        className="btn btn-outline-secondary ml-2"
                        onClick={() => window.location.href = '/admin'}
                      >
                        <i className="fa fa-arrow-left mr-2"></i>
                        Back to Admin
                      </button>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      );
    }

    return (
      <div className="service-monitoring-dashboard">
        <Container fluid>
          {/* Header */}
          <Row className="mb-4">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2>Service Monitoring Dashboard</h2>
                  <p className="text-muted mb-0">
                    Real-time monitoring of Windows background services
                    {lastUpdated && (
                      <span className="ml-2">
                        · Last updated: {lastUpdated.toLocaleTimeString()}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={this.handleRefresh}
                  disabled={loading}
                >
                  <i className="fa fa-refresh mr-2"></i>
                  Refresh
                </button>
              </div>
            </Col>
          </Row>

          {/* Alert Banner (from email redirect) */}
          {showAlertBanner && (
            <Row>
              <Col>
                <AlertBanner
                  serviceName={alertServiceName}
                  alertId={alertId}
                  onDismiss={this.handleDismissAlertBanner}
                />
              </Col>
            </Row>
          )}

          {/* Alert & Email Activity Panel - Tabbed View (includes Email Configuration) */}
          {!loading && !error && (
            <Row>
              <Col>
                <AlertActivityPanel hours={24} />
              </Col>
            </Row>
          )}

          {/* Loading State */}
          {loading && services.length === 0 && (
            <Row>
              <Col className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading service data...</p>
              </Col>
            </Row>
          )}

          {/* Error State */}
          {error && (
            <Row>
              <Col>
                <div className="alert alert-danger" role="alert">
                  <h4 className="alert-heading">Error Loading Services</h4>
                  <p>{error}</p>
                  <hr />
                  <p className="mb-0">
                    Please ensure the API is running and accessible.
                    <button className="btn btn-sm btn-outline-danger ml-3" onClick={this.handleRefresh}>
                      Try Again
                    </button>
                  </p>
                </div>
              </Col>
            </Row>
          )}

          {/* Dashboard Content */}
          {!loading && !error && services.length > 0 && (
            <>
              {/* Summary Cards */}
              <DashboardSummaryCards services={services} />

              {/* Service List Table */}
              <Row className="mt-4">
                <Col>
                  <ServiceListTable
                    services={services}
                    onServiceClick={this.handleServiceClick}
                  />
                </Col>
              </Row>
            </>
          )}

          {/* Empty State */}
          {!loading && !error && services.length === 0 && (
            <Row>
              <Col className="text-center py-5">
                <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                <h4>No Services Configured</h4>
                <p className="text-muted">
                  No monitored services found. Please check the database configuration.
                </p>
              </Col>
            </Row>
          )}

          {/* Service Detail Modal */}
          <ServiceDetailModal
            show={showModal}
            onHide={this.handleCloseModal}
            serviceName={selectedService}
          />
        </Container>
      </div>
    );
  }
}

export default ServiceMonitoringDashboard;
